/**
 * VistoeraTASK-00532 — pure display helpers for 权限管理 / 操作日志.
 * Nothing here authorises anything: the permission matrix and the audit log
 * come from the backend, which enforces every rule again on each request.
 * The audit log is READ ONLY — there is deliberately no helper (and no API
 * function) that edits, deletes or clears an entry.
 */
import type {
  AuditCategory,
  AuditEvent,
  CapabilityGroup,
  PermissionCapability,
  PermissionLevel,
  TeamRoleKey,
} from './teamTypes'

export const AUDIT_CATEGORIES: readonly AuditCategory[] = ['member', 'permission', 'credits', 'team', 'system']

export function isAuditCategory(value: unknown): value is AuditCategory {
  return typeof value === 'string' && (AUDIT_CATEGORIES as readonly string[]).includes(value)
}

/** The tenant's canonical day boundary (never the browser's time zone). */
export const DEFAULT_AUDIT_TIMEZONE = 'Asia/Shanghai'

/** `YYYY-MM-DD` of `date` on the calendar of `timeZone`. */
export function dayInZone(date: Date, timeZone: string = DEFAULT_AUDIT_TIMEZONE): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** Last `days` calendar days INCLUDING today, as inclusive tenant-timezone dates. */
export function defaultAuditRange(now: Date = new Date(), days = 30, timeZone: string = DEFAULT_AUDIT_TIMEZONE): { start: string; end: string } {
  const end = dayInZone(now, timeZone)
  const start = dayInZone(new Date(now.getTime() - (days - 1) * 86_400_000), timeZone)
  return { start, end }
}

/** `YYYY-MM-DD HH:mm:ss` of an instant in `timeZone` ('—' for a missing / invalid value). */
export function formatAuditTime(iso: string | null | undefined, timeZone: string = DEFAULT_AUDIT_TIMEZONE): string {
  if (!iso) return '—'
  const stamped = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`
  const date = new Date(stamped)
  if (Number.isNaN(date.getTime())) return '—'
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`
}

/** Platform operators / system jobs never carry a tenant user identity. */
export function isPlatformActor(role: string | null | undefined): boolean {
  return role === 'platform_admin' || role === 'system'
}

/** Best display name for the actor: current name, else the name AT THE TIME, else null. */
export function actorDisplayName(e: Pick<AuditEvent, 'actor'>): string | null {
  return e.actor.name || e.actor.name_snapshot || null
}

export function targetDisplayName(e: Pick<AuditEvent, 'target'>): string | null {
  return e.target.name || e.target.name_snapshot || null
}

/** Group matrix rows in a stable, product-meaningful order. */
export const CAPABILITY_GROUP_ORDER: readonly CapabilityGroup[] = ['team', 'members', 'credits', 'permissions', 'audit', 'lifecycle', 'future']

export function groupCapabilities(rows: readonly PermissionCapability[]): Array<{ group: CapabilityGroup; rows: PermissionCapability[] }> {
  return CAPABILITY_GROUP_ORDER.map((group) => ({ group, rows: rows.filter((r) => r.group === group) })).filter((g) => g.rows.length > 0)
}

/** What the matrix row means for `role`: only `active` rows are real role-matrix permissions. */
export function effectiveLevel(
  row: PermissionCapability,
  role: TeamRoleKey,
): PermissionLevel | 'soon' | 'unavailable' | 'entry' {
  if (row.status === 'entry') return 'entry'
  if (row.status === 'coming_soon') return 'soon'
  if (row.status === 'not_available') return 'unavailable'
  return row.levels[role]
}

export function isMatrixCellEditable(row: PermissionCapability, role: TeamRoleKey, canEditMatrix: boolean): boolean {
  if (!canEditMatrix || role === 'owner') return false
  return (row.editable_roles ?? []).includes(role)
}

/** Cycle full → limited → none → full (skipping levels the backend rejects). */
export function nextPermissionLevel(current: PermissionLevel, allowed: readonly PermissionLevel[]): PermissionLevel {
  if (!allowed.length) return current
  const idx = allowed.indexOf(current)
  if (idx < 0) return allowed[0]
  return allowed[(idx + 1) % allowed.length]
}

/** Number of enforced capabilities `role` can use (fully or in a restricted way). */
export function grantedCount(rows: readonly PermissionCapability[], role: TeamRoleKey): number {
  return rows.filter((r) => r.status === 'active' && r.levels[role] !== 'none').length
}

/** Detail values the API returns as raw codes -> i18n keys / formatting hints. */
export function detailValueKind(key: string, value: unknown): 'role' | 'limit' | 'bool' | 'text' {
  if (typeof value === 'boolean') return 'bool'
  if (typeof value === 'string' && (key === 'from' || key === 'to' || key === 'role')) {
    if (['owner', 'admin', 'editor', 'viewer', 'member'].includes(value)) return 'role'
    if (value === 'unlimited' || /^\d+(\.\d+)?$/.test(value)) return 'limit'
  }
  return 'text'
}

/** Canonical backend role (`editor` / `viewer`) -> product role key. */
export function productRoleOf(role: string | null | undefined): TeamRoleKey {
  if (role === 'owner') return 'owner'
  if (role === 'admin') return 'admin'
  return 'member'
}

export function auditSourceKey(source: string | null | undefined): 'web' | 'platform' | 'system' | 'unknown' {
  if (source === 'web') return 'web'
  if (source === 'platform_console') return 'platform'
  if (source === 'system') return 'system'
  return 'unknown'
}

export interface AuditFilters {
  range: { start: string; end: string } | null
  actorUserId?: string
  category?: AuditCategory
  result?: 'ok' | 'rejected'
  q?: string
}

/** Query params for GET /audit-logs: empty filters are omitted, never sent as ''. */
export function buildAuditParams(f: AuditFilters, page: number, pageSize: number) {
  const params: Record<string, string | number> = { page, page_size: pageSize }
  if (f.range) {
    params.start_date = f.range.start
    params.end_date = f.range.end
  }
  if (f.actorUserId) params.actor_user_id = f.actorUserId
  if (f.category && isAuditCategory(f.category)) params.category = f.category
  if (f.result) params.result = f.result
  const q = (f.q ?? '').trim()
  if (q) params.q = q.slice(0, 100)
  return params
}
